<template>
  <v-dialog :value="dialog" max-width="1000" persistent>
    <v-card :disabled="loading">
      <v-card-title class="headline accent--text pb-4">
        Личные данные
      </v-card-title>

      <v-card-text outlined tile class="border-top pt-6 pb-0">
        <v-row>
          <v-col cols="12" class="pb-0">
            <v-autocomplete
              v-model="localPersonalData.gender"
              :items="dictionaries.gender"
              item-text="name"
              placeholder="Выберите данные"
              no-data-text="Не найдено"
              clearable
              outlined
              rounded
              dense
              return-object
              @input="$v.localPersonalData.gender.$touch()"
              @blur="$v.localPersonalData.gender.$touch()"
              :error-messages="$errorMessages($v.localPersonalData.gender)"
            >
              <template #label>
                <span class="required">Пол</span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12">
            <PersonalNameComponent
              :personal-name="localPersonalName"
              @set-personal-name="setPersonalName"
            />
          </v-col>

          <v-col cols="12">
            <PersonalContactComponent
              :personal-contact="localPersonalContact"
              @set-personal-contact="setPersonalContact"
            />
          </v-col>

          <v-col cols="12">
            <AddressGarComponent
              :address-gar-parent="localAddressGarParent"
              @set-address-gar-parent="setAddressGarParent"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="px-6">
        <v-spacer></v-spacer>
        <v-btn
          text
          color="lightGrey"
          :disabled="!personalData.id"
          @click="close"
        >
          Отмена
        </v-btn>

        <v-btn
          :loading="loading"
          :disabled="$v.$invalid || !localAddressGarParent.list.length"
          color="success"
          text
          @click="saveData"
        >
         Сохранить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PersonalNameComponent from '@/components/zvp/personal-data/PersonalName'
import PersonalContactComponent from '@/components/zvp/personal-data/PresonalContact'
import AddressGarComponent from "@/components/zvp/personal-data/address-gar/Index"
import {mapGetters, mapActions, mapState} from 'vuex'
import {PersonalData, PersonalName, PersonalContact, AddressGarParent} from '@/models'
import {addressGar, personalContact, personalData, personalName} from "@/scripts/rules-validation"
import _ from "lodash";

  export default {
    name: 'PersonalData',

    components: {
      PersonalNameComponent,
      PersonalContactComponent,
      AddressGarComponent
    },

    props: {
      dialog: Boolean,
    },

    data() {
      return {
        loading: false,
        localPersonalData: new PersonalData(),
        localPersonalName: new PersonalName(),
        localPersonalContact: new PersonalContact(),
        localAddressGarParent: new AddressGarParent()
      }
    },

    validations() {
      return {
        localPersonalData: { ...personalData },
        localPersonalName: { ...personalName },
        localPersonalContact: { ...personalContact },
        localAddressGarParent: {
          list: {
            $each: { ...addressGar }
          }
        },
        validationGroup: ['localPersonalData', 'localPersonalName', 'localPersonalContact', 'localAddressGarParent']
      }
    },

    computed: {
      ...mapGetters('dictionaries', ['dictionaries']),
      ...mapGetters('personalData', ['personalDataId']),
      ...mapState('system', ['dataLoaded']),
      ...mapState('personalData', ['personalData']),
      ...mapState('personalName', ['personalName']),
      ...mapState('personalContact', ['personalContact']),
      ...mapState('addressGar', ['addressGarParent']),
    },

    methods: {
      ...mapActions('personalData', ['UPDATE_PERSONAL_DATA', 'CREATE_PERSONAL_DATA']),
      ...mapActions('personalName', ['UPDATE_PERSONAL_NAME', 'CREATE_PERSONAL_NAME']),
      ...mapActions('personalContact', ['UPDATE_PERSONAL_CONTACT', 'CREATE_PERSONAL_CONTACT']),
      ...mapActions('addressGar', ['UPDATE_ADDRESS_GAR', 'CREATE_ADDRESS_GAR']),

      async saveData() {
        try {
          this.loading = true
          const data = await this.savePersonalData()
          data.id && await Promise.all([this.savePersonalName(), this.savePersonalContact(), this.saveAddressGar()])
          this.$toastr('success', '', 'Успешно сохранено')
          this.close()
        } catch (e) {
          this.showErrorMessage(e)
        } finally {
          this.loading = false
        }
      },

      async savePersonalData() {
        try {
          const data = this.localPersonalData.id
            ? await this.UPDATE_PERSONAL_DATA(this.localPersonalData)
            : await this.CREATE_PERSONAL_DATA(this.localPersonalData)
          this.localPersonalData = PersonalData.build(data)
          return data
        } catch (e) {
          return Promise.reject(e)
        }
      },

      async savePersonalName() {
        try {
          this.localPersonalName.personalData = this.personalDataId
          const data = this.localPersonalName.id
            ? await this.UPDATE_PERSONAL_NAME(this.localPersonalName)
            : await this.CREATE_PERSONAL_NAME(this.localPersonalName)
          this.localPersonalName = PersonalName.build(data)
        } catch (e) {
          return Promise.reject(e)
        }
      },

      async savePersonalContact() {
        try {
          this.localPersonalContact.personalData = this.personalDataId
          const data = this.localPersonalContact.id
            ? await this.UPDATE_PERSONAL_CONTACT(this.localPersonalContact)
            : await this.CREATE_PERSONAL_CONTACT(this.localPersonalContact)
          this.localPersonalContact = PersonalContact.build(data)
        } catch (e) {
          return Promise.reject(e)
        }
      },

      async saveAddressGar() {
        try {
          for (const item of this.localAddressGarParent.list) {
            item.personalData = this.personalDataId
            const data = item.id ? await this.UPDATE_ADDRESS_GAR(item) : await this.CREATE_ADDRESS_GAR(item)
            item.setProperties(data)
          }
        } catch (e) {
          return Promise.reject(e)
        }
      },

      setPersonalName(event) {
        this.localPersonalName = event
      },

      setPersonalContact(event) {
        this.localPersonalContact = event
      },

      setAddressGarParent(event) {
        this.localAddressGarParent = event
      },

      close() {
        this.$emit('close')
      },
    },

    watch: {
      'personalData.id': {
        immediate: true,
        handler() {
          this.localPersonalData = PersonalData.build(this.personalData)
        }
      },

      'personalName.id': {
        immediate: true,
        handler() {
          this.localPersonalName = PersonalName.build(this.personalName)
        }
      },

      'personalContact.id': {
        immediate: true,
        handler() {
          this.localPersonalContact = PersonalContact.build(this.personalContact)
        }
      },

      dataLoaded: {
        immediate: true,
        handler(value) {
          if (value) {
            this.localAddressGarParent = _.cloneDeep(this.addressGarParent)
          }
        }
      },
    }
  }
</script>
<style scoped>
  .border-top {
    border: 0 !important;
    border-top: 1px solid rgba(0, 0, 0, 0.12) !important;
  }

  .theme--light.v-btn.v-btn--disabled {
    color: #dadada !important
  }
</style>
